import { URL } from "url";
import { posix } from "path";

export interface PathTransformerConfig {
  /** The prefix path that should be added/removed when transforming paths */
  pathPrefix: string;
}

export class PathTransformer {
  private readonly pathPrefix: string;
  private readonly normalizedPrefix: string;

  constructor(config: PathTransformerConfig) {
    this.pathPrefix = config.pathPrefix;
    // Normalize the prefix to ensure it starts with / and doesn't end with /
    this.normalizedPrefix = this.normalizePath(config.pathPrefix);
  }

  /**
   * Transform a message from server to client by removing path prefix from URIs
   */
  serverToClient<T = any>(message: T): T {
    return this.transformMessage(message, (uri) => this.removePrefix(uri));
  }

  /**
   * Transform a message from client to server by adding path prefix to URIs
   */
  clientToServer<T = any>(message: T): T {
    return this.transformMessage(message, (uri) => this.addPrefix(uri));
  }

  /**
   * Remove the path prefix from a URI string
   */
  private removePrefix(uriString: string): string {
    try {
      // Handle file:// URIs
      if (uriString.startsWith("file://")) {
        const url = new URL(uriString);
        if (url.pathname.startsWith(this.normalizedPrefix)) {
          const relativePath = url.pathname.substring(
            this.normalizedPrefix.length,
          );
          // Ensure the result starts with /
          const newPath = relativePath.startsWith("/")
            ? relativePath
            : "/" + relativePath;
          url.pathname = newPath;
          return url.toString();
        }
        return uriString;
      }

      // Handle simple file paths (non-URI format)
      if (uriString.startsWith(this.normalizedPrefix)) {
        const relativePath = uriString.substring(this.normalizedPrefix.length);
        return relativePath.startsWith("/") ? relativePath : "/" + relativePath;
      }

      return uriString;
    } catch (error) {
      // If URI parsing fails, return original string
      return uriString;
    }
  }

  /**
   * Add the path prefix to a URI string
   */
  private addPrefix(uriString: string): string {
    try {
      // Handle file:// URIs
      if (uriString.startsWith("file://")) {
        const url = new URL(uriString);
        // Combine prefix with the current path
        const fullPath = posix.join(this.normalizedPrefix, url.pathname);
        url.pathname = fullPath;
        return url.toString();
      }

      // Handle simple file paths (non-URI format)
      if (uriString.startsWith("/")) {
        return posix.join(this.normalizedPrefix, uriString);
      }

      return uriString;
    } catch (error) {
      // If URI parsing fails, return original string
      return uriString;
    }
  }

  /**
   * Recursively transform all URI fields in a message
   */
  private transformMessage<T>(
    message: T,
    transformer: (uri: string) => string,
  ): T {
    if (message === null || message === undefined) {
      return message;
    }

    if (Array.isArray(message)) {
      return message.map((item) =>
        this.transformMessage(item, transformer),
      ) as unknown as T;
    }

    if (typeof message === "object" && !Array.isArray(message)) {
      const result = { ...message } as any;

      for (const [key, value] of Object.entries(message)) {
        if (this.isUriField(key)) {
          // This is a known URI field, transform it
          if (typeof value === "string") {
            result[key] = transformer(value);
          } else if (Array.isArray(value)) {
            // Handle arrays of URIs
            result[key] = value.map((item) =>
              typeof item === "string" ? transformer(item) : item,
            );
          } else {
            // Keep non-string values as-is
            result[key] = value;
          }
        } else {
          // Recursively transform nested objects
          result[key] = this.transformMessage(value, transformer);
        }
      }

      return result;
    }

    // For primitive types (string, number, boolean), return as-is
    return message;
  }

  /**
   * Check if a field name indicates it contains a URI
   */
  private isUriField(fieldName: string): boolean {
    const uriFields = [
      // Standard LSP URI fields
      "uri",
      "targetUri",
      "documentUri",
      "rootUri",
      "workspaceUri",
      "configurationUri",
      "schemaUri",
      "baseUri",

      // File operation fields
      "oldUri",
      "newUri",
      "sourceUri",
      "destinationUri",
    ];

    // Check for exact matches
    if (uriFields.includes(fieldName)) {
      return true;
    }

    // Check for fields ending with 'Uri' or 'Uris'
    if (fieldName.endsWith("Uri") || fieldName.endsWith("Uris")) {
      return true;
    }

    return false;
  }

  /**
   * Normalize a path to ensure consistent format
   */
  private normalizePath(path: string): string {
    if (!path) return "";

    // Ensure path starts with /
    let normalized = path.startsWith("/") ? path : "/" + path;

    // Remove trailing / (except for root)
    if (normalized.length > 1 && normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }

    return normalized;
  }
}

/**
 * Create a path transformer instance with the given configuration
 */
export function createPathTransformer(
  config: PathTransformerConfig,
): PathTransformer {
  return new PathTransformer(config);
}

/**
 * Utility function to transform server-to-client messages
 */
export function transformServerToClient<T>(message: T, pathPrefix: string): T {
  const transformer = createPathTransformer({ pathPrefix });
  return transformer.serverToClient(message);
}

/**
 * Utility function to transform client-to-server messages
 */
export function transformClientToServer<T>(message: T, pathPrefix: string): T {
  const transformer = createPathTransformer({ pathPrefix });
  return transformer.clientToServer(message);
}
